import React from "react";
import { Row, Col, Label, Input, Button } from "reactstrap";

export type ProcessStepForm = { title: string; text: string };

type ProcessStepsSectionProps = {
  processSteps: ProcessStepForm[];
  setProcessSteps: (steps: ProcessStepForm[]) => void;
};

/**
 * Ordered process steps (title + narrative) for product traceability stories.
 */
export function ProcessStepsSection(props: ProcessStepsSectionProps) {
  const { processSteps, setProcessSteps } = props;
  const list = Array.isArray(processSteps) ? processSteps : [];

  const updateStep = (index: number, field: keyof ProcessStepForm, value: string) => {
    const next = [...list];
    next[index] = { ...next[index], [field]: value };
    setProcessSteps(next);
  };

  const removeStep = (index: number) => {
    const next = [...list];
    next.splice(index, 1);
    setProcessSteps(next);
  };

  const moveStep = (index: number, delta: number) => {
    const j = index + delta;
    if (j < 0 || j >= list.length) return;
    const next = [...list];
    const t = next[index];
    next[index] = next[j];
    next[j] = t;
    setProcessSteps(next);
  };

  if (!list.length) {
    return null;
  }

  return (
    <div className="formDiv">
      {list.map((step, index) => (
        <Row key={`process-step-${index}`} className="mb-3 align-items-start">
          <Col xs={12} md={3}>
            <Label className="small">Step title</Label>
            <Input
              type="text"
              className="form-control"
              placeholder="e.g. Fabric sourcing"
              value={step.title}
              onChange={(e) => updateStep(index, "title", e.target.value)}
            />
          </Col>
          <Col xs={12} md={6}>
            <Label className="small">Note</Label>
            <Input
              type="textarea"
              className="form-control"
              rows={2}
              placeholder="What happened in this step?"
              value={step.text}
              onChange={(e) => updateStep(index, "text", e.target.value)}
            />
          </Col>
          <Col xs={12} md={3} className="d-flex flex-row flex-nowrap gap-1 mt-2 mt-md-4 pt-md-2 hkp-process-actions">
            <Button
              size="sm"
              color="secondary"
              outline
              type="button"
              disabled={index === 0}
              onClick={() => moveStep(index, -1)}
            >
              Move up
            </Button>
            <Button
              size="sm"
              color="secondary"
              outline
              type="button"
              disabled={index >= list.length - 1}
              onClick={() => moveStep(index, 1)}
            >
              Move down
            </Button>
            <Button size="sm" color="danger" outline type="button" onClick={() => removeStep(index)}>
              Remove
            </Button>
          </Col>
        </Row>
      ))}
    </div>
  );
}

export function serializeProcessStepsForApi(raw: ProcessStepForm[] | undefined) {
  const list = Array.isArray(raw) ? raw : [];
  return list
    .map((s) => ({
      title: String(s?.title ?? "").trim(),
      text: String(s?.text ?? "").trim(),
    }))
    .filter((s) => s.title.length > 0 || s.text.length > 0);
}

export function parseProcessStepsFromApi(raw: unknown): ProcessStepForm[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((s: any) => ({
    title: s?.title != null ? String(s.title) : "",
    text: s?.text != null ? String(s.text) : s?.description != null ? String(s.description) : "",
  }));
}
